import { polarToVec } from "../../core/util/MathUtil";
import { choose, rUniform } from "../../core/util/Random";
import { V2d } from "../../core/Vector";

export enum AsteroidSize {
  SMALL,
  MEDIUM,
  LARGE,
  XL,
  XXL,
}

// Asteroid size configuration
export const ASTEROID_CONFIG = {
  [AsteroidSize.SMALL]: {
    health: 2, // Reduced from 4 for easier killing
    baseRadius: 8,
    radiusVariation: 2,
    vertexCount: [6, 7, 8],
    fragmentCount: 0, // No fragments for small asteroids
    points: 10,
  },
  [AsteroidSize.MEDIUM]: {
    health: 8,
    baseRadius: 13,
    radiusVariation: 3,
    vertexCount: [7, 8, 9],
    fragmentCount: [2, 3],
    points: 20,
  },
  [AsteroidSize.LARGE]: {
    health: 12,
    baseRadius: 20,
    radiusVariation: 4,
    vertexCount: [8, 9, 10],
    fragmentCount: [2, 3],
    points: 50,
  },
  [AsteroidSize.XL]: {
    health: 16,
    baseRadius: 28,
    radiusVariation: 5,
    vertexCount: [9, 10, 11],
    fragmentCount: [2, 3],
    points: 100,
  },
  [AsteroidSize.XXL]: {
    health: 24,
    baseRadius: 36,
    radiusVariation: 6,
    vertexCount: [10, 11, 12],
    fragmentCount: [2, 3],
    points: 200,
  },
} as const;

export interface AsteroidShape {
  points: V2d[];
  size: AsteroidSize;
  radius: number; // For physics collision detection
}

/**
 * Generate a random irregular polygon for an asteroid
 * @param size The asteroid size
 * @returns An asteroid shape with irregular polygon points
 */
export function generateRandomAsteroidShape(size: AsteroidSize): AsteroidShape {
  const config = ASTEROID_CONFIG[size];
  const vertexCount = choose(...config.vertexCount);
  const points: V2d[] = [];

  // Calculate physics radius (largest distance from center)
  let maxRadius = 0;

  for (let i = 0; i < vertexCount; i++) {
    const angle = (i / vertexCount) * Math.PI * 2;

    // Add random variation to radius for irregular shape
    const radiusVariation = rUniform(
      -config.radiusVariation,
      config.radiusVariation,
    );
    const radius = config.baseRadius + radiusVariation;
    maxRadius = Math.max(maxRadius, radius);

    // Add small angular variation for more natural look
    const angleVariation = rUniform(-0.3, 0.3);
    const finalAngle = angle + angleVariation;

    const point = polarToVec(finalAngle, radius);
    points.push(point);
  }

  return {
    points,
    size,
    radius: maxRadius,
  };
}

/**
 * Get the smaller asteroid size for fragmentation
 * @param currentSize The current asteroid size
 * @returns The smaller size, or null if the asteroid shouldn't fragment
 */
export function getSmallerAsteroidSize(
  currentSize: AsteroidSize,
): AsteroidSize | null {
  switch (currentSize) {
    case AsteroidSize.SMALL:
      return null; // Small asteroids don't fragment
    case AsteroidSize.MEDIUM:
      return AsteroidSize.SMALL;
    case AsteroidSize.LARGE:
      return AsteroidSize.MEDIUM;
    case AsteroidSize.XL:
      return AsteroidSize.LARGE;
    case AsteroidSize.XXL:
      return AsteroidSize.XL;
    default:
      return null;
  }
}
